import { useState } from 'react';
import { User } from '../../../clients/quipli-service/user/user';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { Typography } from '../../../components/typography/typography';
import { RepresentativeSelect } from '../../customer/components/representative-select';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { toast } from '../../../util/toasts';
import { formatPhoneNumber } from '../../../util/util';
import { Representative } from '../../../clients/quipli-service/representatives/representative';

export const CustomerRepresentative = ({
  customer,
  representative,
  setRepresentative,
  isCreateMode = false,
  isOrderEditing = false,
  orderId,
}: {
  customer: User;
  representative?: Representative;
  setRepresentative: (r?: Representative) => void;
  isCreateMode: boolean;
  isOrderEditing: boolean;
  orderId?: number;
}) => {
  return customer.structureType == 'company' ? (
    <div className="border-t border-solid border-x-0 border-b-0 border-t-grey-200 pt-[12px]">
      <div className="pb-[8px]">
        <Typography type="text-md" weight="medium" className="text-grey-900">
          Representative
        </Typography>
      </div>

      {isCreateMode ? (
        <CreateOrderRepresentative
          customerId={customer.id}
          representative={representative}
          setRepresentative={setRepresentative}
        />
      ) : (
        orderId && (
          <EditOrderRepresentative
            orderId={orderId}
            customerId={customer.id}
            customer={customer}
            isOrderEditing={isOrderEditing}
            representative={representative}
            setRepresentative={setRepresentative}
          />
        )
      )}
    </div>
  ) : null;
};

const CreateOrderRepresentative = ({
  customerId,
  representative,
  setRepresentative,
}: {
  customerId: number;
  representative?: Representative;
  setRepresentative: (r?: Representative) => void;
}) => {
  return !representative ? (
    <RepresentativeSelect userId={customerId} setRepresentative={setRepresentative} />
  ) : (
    <div className="flex justify-between items-center">
      <div className="flex flex-col gap-y-[8px]">
        <Typography type="text-sm" className="text-grey-800">
          {representative!.name}
        </Typography>
        {representative!.title && (
          <Typography type="text-sm" className="text-grey-800">
            {representative!.title}
          </Typography>
        )}
        <Typography type="text-sm" className="text-grey-800">
          {representative!.email}
        </Typography>
        {representative!.phone && (
          <Typography type="text-sm" className="text-grey-800">
            {formatPhoneNumber(representative!.phone)}
          </Typography>
        )}
      </div>

      <Button className="button icon-button !py-0" onClick={() => setRepresentative(undefined)}>
        <Icon icon="Trash" />
      </Button>
    </div>
  );
};

const EditOrderRepresentative = ({
  orderId,
  customerId,
  customer,
  isOrderEditing,
  representative,
  setRepresentative,
}: {
  orderId: number;
  customerId: number;
  customer: User;
  isOrderEditing: boolean;
  representative?: Representative;
  setRepresentative: (r?: Representative) => void;
}) => {
  const { updateOrderRepresentative } = useAPI();

  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRepresentative, setSelectedRepresentative] = useState<Representative>();

  const submit = async () => {
    if (customer && customer.requireCompanyRep && !selectedRepresentative) {
      toast('Please select a representative.', 'error');
      return;
    }

    setIsLoading(true);

    try {
      await updateOrderRepresentative(orderId, selectedRepresentative?.id);
      setIsEdit(false);
      toast('Representative updated.');
      setRepresentative(selectedRepresentative);
    } catch (err) {
      toast('Failed to update representative.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return !isEdit ? (
    <div className="flex justify-between items-center">
      <div className="flex flex-col gap-y-[8px]">
        {representative ? (
          <>
            <Typography type="text-sm" className="text-grey-800">
              {representative.name}
            </Typography>
            {representative.title && (
              <Typography type="text-sm" className="text-grey-800">
                {representative.title}
              </Typography>
            )}
            <Typography type="text-sm" className="text-grey-800">
              {representative.email}
            </Typography>
            {representative.phone && (
              <Typography type="text-sm" className="text-grey-800">
                {formatPhoneNumber(representative!.phone)}
              </Typography>
            )}
          </>
        ) : (
          <Typography type="text-sm" className="text-grey-800">
            N/A
          </Typography>
        )}
      </div>

      {!isOrderEditing && (
        <Button className="button icon-button !py-0" onClick={() => setIsEdit(true)}>
          <Icon icon="Edit" />
        </Button>
      )}
    </div>
  ) : (
    <div className="flex flex-col gap-[8px]">
      <RepresentativeSelect
        userId={customerId}
        setRepresentative={setSelectedRepresentative}
        isDisabled={isOrderEditing || isLoading}
      />

      <div className="flex justify-end gap-[8px]">
        <Button
          className="button button-secondary"
          onClick={() => setIsEdit(false)}
          disabled={isOrderEditing || isLoading}
        >
          Cancel
        </Button>

        <Button
          className="button button-primary"
          onClick={submit}
          disabled={isOrderEditing || isLoading || selectedRepresentative === representative}
        >
          Save
        </Button>
      </div>
    </div>
  );
};
